import React, { useEffect, useState, useRef } from "react";
import { Box, Card, CardContent, Typography, Button, Divider, Stack, DialogActions, DialogContent, DialogContentText, Dialog, DialogTitle } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth0 } from "@auth0/auth0-react";
import { getStore, saveStore } from "../storage/hearingTestStore.js";
import { mapPatientToRedcapPayload, mapResultsToRedcapPayload } from "../models/redcapDraft.js";
import {
  Radio,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import "../styles/pendingEntries.css";

const PendingEntries = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [mergePopUp, setMergePopUp] = useState(false);
  const [matchedPatients, setMatchedPatients] = useState([]); // array of rows
  const [selectedPatientId, setSelectedPatientId] = useState(null); // patient_id of chosen row


  const [uploadQueue, setUploadQueue] = useState([]);
  const [deletePopUp, setDeletePopUp] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const skipFirstSave = useRef(false);

  const currentObject = uploadQueue[currentIndex] ?? null;

  useEffect(() => {
    const store = getStore();
    setUploadQueue(store.uploadQueue ?? []);
  }, []);

  useEffect(() => {
    if (skipFirstSave.current === false) {
      skipFirstSave.current = true;
      return;
    }
    const prev = getStore();
    saveStore({ ...prev, uploadQueue })
  }, [uploadQueue]);

  function getName(patientinfo) {

    if (!patientinfo) {
      return '';
    }
    const { first_name, middle_name, last_name } = patientinfo.demographics;

    return [first_name, middle_name, last_name].filter(Boolean).join(" ");

  }

  function getMatchedName(patientinfo) {

    if (!patientinfo) {
      return '';
    }
    const { first_name, middle_name, last_name } = patientinfo;

    return [first_name, middle_name, last_name].filter(Boolean).join(" ");

  }

  function getMatchedAddress(patientinfo) {
    if (!patientinfo) {
      return '';
    }
    const { street_address, city, state, zip } = patientinfo;

    const address = street_address + ', ' + city + ', ' + state + ' ' + zip;
    return address;
  }

  function getAddress(patientinfo) {
    if (!patientinfo) {
      return '';
    }
    const { street_address, city, state, zip } = patientinfo.demographics;

    const address = street_address + ', ' + city + ', ' + state + ' ' + zip;
    return address;
  }
  async function patientPost() {
    const payload = mapPatientToRedcapPayload(currentObject);
    const token = await getAccessTokenSilently({ authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE } });
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URI}/api/patients`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data?.detail || data?.error || 'Submission Failed');
    }
    const first = Array.isArray(data) ? data[0] : data;
    const [idStr] = String(first).split(',');
    const id = Number(idStr)
    return id;


  }
  async function resultPost(patientId = null) {

    const payload = mapResultsToRedcapPayload(currentObject, patientId);

    const token = await getAccessTokenSilently({ authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE } });
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URI}/api/results`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data?.detail || data?.error || 'Submission Failed');
    }

  }
  // get matching entry based on first name, last name, DOB
  async function getMatchingPatients() {
    const d = currentObject.demographics;

    const params = new URLSearchParams({
      first_name: d.first_name,
      middle_name: d.middle_name,
      last_name: d.last_name,
      dob: d.dob,
    });

    const token = await getAccessTokenSilently({
      authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
    });

    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URI}/api/patients?${params.toString()}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    const rows = await res.json();
    if (!res.ok) throw new Error("Patient lookup failed");

    // 1) No matches
    if (rows.length === 0) {
      return [];
    }




    return Array.isArray(rows) ? rows : [];
  }


  async function handleCreateNewPatient() {
    try {
      let patientId = null;
      patientId = await patientPost();
      await resultPost(patientId);

      setMergePopUp(false);
      alert("Upload successful.")
      handleConfirmDelete(currentIndex);
    } catch (error) {
      setMergePopUp(false);
      console.error("Upload failed:", error);
      alert(`Upload failed. Please try again at a later time: \n${error.message}`);
    }

  }
  async function handleExistingPatient() {
    try {
      if (!selectedPatientId) return;
      await resultPost(selectedPatientId);
      setMergePopUp(false);
      alert("Upload successful.");
      handleConfirmDelete(currentIndex);
    } catch (error) {
      setMergePopUp(false);
      console.error("Upload failed:", error);
      alert(`Upload failed. Please try again at a later time: \n${error.message}`);
    }
  }

  async function handleUpload() {
    // if a user with the same first name, last name, DOB is found
    let matchedPatientCheck = [];
    try {
      matchedPatientCheck = await getMatchingPatients();
      setMatchedPatients(matchedPatientCheck);
    } catch (error) {
      console.error("Upload failed:", error);
      alert(`Upload failed. Please try again at a later time: \n${error.message}`);
      return;
    }


    let patientId = null;

    if (matchedPatientCheck.length === 0) {
      try {
        patientId = await patientPost();

        await resultPost(patientId);

        handleConfirmDelete(currentIndex);
        alert("Upload successful.")
      } catch (error) {
        console.error("Upload failed:", error);
        alert(`Upload failed. Please try again at a later time: \n${error.message}`);
      }
    } else {
      setMergePopUp(true);
    }
  }

  function getSex(index) {
    switch (index) {
      case "1":
        return "Male";
      case "2":
        return "Female";
      case "3":
        return "Trans Male";
      case "4":
        return "Trans Female";
      default:
        return "Not Provided";
    }
  }

  function getRace(index) {
    switch (index) {
      case "1":
        return "White";
      case "2":
        return "Black/African American";
      case "3":
        return "Hispanic/Latin-American";
      case "4":
        return "Native/Indigenous";
      case "5":
        return "Asian";
      case "6":
        return "Native Hawaiian/Pacific Islander";
      default:
        return 'Not Provided';
    }
  }

  function getEducationLevel(index) {
    switch (index) {
      case "1":
        return "Less than high school diploma";
      case "2":
        return "High School diploma/GED";
      case "3":
        return "Some college or trade school";
      case "4":
        return "Associate's degree";
      case "5":
        return "Bachelor's degree";
      case "6":
        return "Graduate/professional degree (M.D., Ph.D., J.D., etc.)";
      default:
        return 'Not Provided';
    }
  }



  function handleConfirmDelete(indexToRemove) {
    setUploadQueue(prev => {
      const next = prev.filter((_, i) => i !== indexToRemove);

      setCurrentIndex(i => {
        if (next.length === 0) return 0;

        const backOne = Math.max(0, i - 1);
        return Math.min(backOne, next.length - 1);
      });

      return next;
    });
  }

  const InfoRow = ({ label, value }) => (
    <Box>
      <Typography
        variant="caption"
        className="pending-entries__infoRow"
      >
        {label}
      </Typography>

      <Typography
        variant="body1"
        className="pending-entries__infoValue"
      >
        {value || "—"}
      </Typography>
    </Box>
  );

  // If there are no entries
  if (uploadQueue.length === 0) {
    return (
      <Box className="pending-entries pending-entries--empty" >
        <Typography variant="h5">No pending entries</Typography>
      </Box>
    );
  }

  return (


    <Box
      className="pending-entries" >
      <Card
        className="pending-entries__card"
      >

        {/* Accent Bar */}
        <Box className="pending-entries__accentBar" />

        <CardContent className="pending-entries__cardContent">
          <Typography
            variant="h6"
            className="pending-entries__title"
          >
            Patient Information
          </Typography>

          <Stack spacing={1.5}>
            <InfoRow
              label="Name"
              value={getName(currentObject)}
            />

            <InfoRow
              label="Date of Birth"
              value={currentObject.demographics.dob}
            />

            <InfoRow
              label="Phone Number"
              value={currentObject.demographics.phone}
            />

            <InfoRow
              label="Address"
              value={getAddress(currentObject)}
            />

            <InfoRow
              label="Sex"
              value={getSex(currentObject.demographics.sex)}
            />

            <InfoRow
              label="Race / Ethnicity"
              value={getRace(currentObject.demographics.race)}
            />

            <InfoRow
              label="Highest Educational Level"
              value={getEducationLevel(currentObject.demographics.highest_education_level)}
            />


            <Divider />

            <InfoRow
              label="Test Type"
              value={currentObject.test.testType}
            />

            <InfoRow
              label="Test Date"
              value={currentObject.meta.createdAt}
            />
          </Stack>
        </CardContent>
      </Card>


      <Box className="pending-entries__actions">
        <Button data-cy="pending_entries_upload_btn" variant="outlined" className="pending-entries__actionBtn" onClick={() => handleUpload()}> Upload </Button>
        <Button data-cy="pending_entries_delete_btn" variant="outlined" className="pending-entries__actionBtn" onClick={() => setDeletePopUp(true)}> Delete </Button>
      </Box>
      <Dialog open={deletePopUp} onClose={() => setDeletePopUp(false)}>
        <DialogTitle>Delete entry?</DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this data entry? Once deleted, you will not be
            able to recover it.
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button data-cy="pending_entries_cancel_delete_btn" onClick={() => setDeletePopUp(false)}>
            Cancel
          </Button>

          <Button
            data-cy="pending_entries_verify_delete_btn"
            variant="contained"
            className="pending-entries__dangerBtn"
            onClick={() => {
              handleConfirmDelete(currentIndex);
              setDeletePopUp(false);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box className="pending-entries__pager">
        {currentIndex > 0 && (
          <Button variant="outlined" data-cy="pending_entries_previous_arrow_btn"
            onClick={() => setCurrentIndex((i) => i - 1)}
            className="pending-entries__arrowBtn"
          >
            <ArrowBackIcon />
          </Button>
        )}

        <Typography variant="h6"> Data entry {currentIndex + 1}/{uploadQueue.length} </Typography>

        <Button data-cy="pending_entries_next_arrow_btn" variant="outlined"
          disabled={currentIndex === uploadQueue.length - 1}
          onClick={() => setCurrentIndex((i) => i + 1)}
          className="pending-entries__arrowBtn"
        >
          <ArrowForwardIcon />
        </Button>
      </Box>
      <Dialog
        open={mergePopUp}
        onClose={() => {
          setMergePopUp(false);
          setSelectedPatientId(null);
          setMatchedPatients([]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="pending-entries__dialogTitle">
          Existing patient found
        </DialogTitle>

        <DialogContent>
          <DialogContentText className="pending-entries__dialogText">
            We found {matchedPatients.length} matching patient(s). Select one to attach today’s test,
            or create a brand-new patient.
          </DialogContentText>

          <FormControl component="fieldset" className="pending-entries__formControl">
            <FormLabel className="pending-entries__formLabel">
              Select an existing patient
            </FormLabel>

            <RadioGroup
              value={selectedPatientId ?? ""}
              onChange={(e) => setSelectedPatientId(e.target.value)}
            >
              <Stack spacing={1.5}>
                {matchedPatients.map((p) => {
                  const id = String(p.patient_id); // normalize to string for RadioGroup
                  const isSelected = String(selectedPatientId) === id;

                  return (
                    <Card
                      key={id}
                      variant="outlined"
                      onClick={() => setSelectedPatientId(id)}
                      className={`pending-entries__matchCard ${isSelected ? 'is-selected' : ''}`}
                    >
                      {/* Accent bar */}
                      <Box className={`pending-entries__matchAccent ${isSelected ? 'is-selected' : ''}`} />

                      <CardContent className="pending-entries__matchContent">
                        <FormControlLabel
                          value={id}
                          control={<Radio />}
                          className="pending-entries__matchLabel"
                          label={
                            <Box className="pending-entries__matchText">
                              <Typography className="pending-entries__matchName">
                                {getMatchedName(p) || "Unnamed"} • ID: {id}
                              </Typography>

                              <Typography variant="body2" className="pending-entries__muted">
                                DOB: {p.dob || "—"} • Phone: {p.phone || "—"}
                              </Typography>

                              <Typography variant="body2" className="pending-entries__muted">
                                {getMatchedAddress(p) || "—"}
                              </Typography>
                            </Box>
                          }
                        />
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            </RadioGroup>
          </FormControl>

          <Divider className="pending-entries__dialogDivider" />

          <Typography variant="body2" className="pending-entries__muted">
            This will only attach the test to the selected patient. It won’t overwrite their existing demographics.
          </Typography>
        </DialogContent>

        <DialogActions className="pending-entries__dialogActions">
          <Button
            onClick={() => {
              setMergePopUp(false);
              setSelectedPatientId(null);
              setMatchedPatients([]);
            }}
          >
            Cancel
          </Button>

          <Button
            variant="outlined"
            onClick={() => handleCreateNewPatient()}
          >
            Create new patient
          </Button>

          <Button
            variant="contained"
            className="pending-entries__dangerBtn"
            disabled={!selectedPatientId}
            onClick={() => handleExistingPatient()}
          >
            Attach test to selected patient
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default PendingEntries;
